import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { defaultYear } from '../../common/constant';
import { constructMaskId } from '../../common/constructMaskId';
import { RootState } from '../../rootReducer';

interface StatisticsOption {
  // Analysis mode.
  analysisMode: 'maskstat' | 'list';

  // MaskStat analysis.
  maskLevelForMaskStat: number;
  maskStatOption: 'ave' | 'sum' | 'max' | 'min';
  maskValuesForMaskStat: {
    [prop: number]: number;
  };
  maskResolutionIdsForMaskStat: {
    [prop: number]: number;
  };
  periodAverageEnabled: boolean;
  yearMax: number;
  yearMin: number;

  // List analysis.
  listOption: 'sum' | 'pergrid' | 'weight';
  maskLevelForList: number;
  maskValuesForList: {
    [prop: number]: number;
  };
  maskResolutionIdsForList: {
    [prop: number]: number;
  };
  time: string; // 'yyyy-MM-dd', 'yyyy-MM', or 'yyyy',
}

const initialState: StatisticsOption = {
  // Analysis mode.
  analysisMode: 'maskstat',

  // MaskStat analysis.
  maskLevelForMaskStat: 0,
  maskStatOption: 'ave',
  maskValuesForMaskStat: {},
  maskResolutionIdsForMaskStat: {},
  periodAverageEnabled: false,
  yearMin: defaultYear,
  yearMax: defaultYear,

  // List analysis.
  listOption: 'sum',
  maskLevelForList: -1,
  maskValuesForList: {'-1': -1},
  maskResolutionIdsForList: {},
  time: '1986-01-01',
};

const statisticsOptionSlice = createSlice({
  name: 'statisticsOption',
  initialState,
  reducers: {
    // Analysis mode.
    updateAnalysisMode(state, action: PayloadAction<{analysisMode: StatisticsOption['analysisMode']}>) {
      const { analysisMode } = action.payload;
      state.analysisMode = analysisMode;
    },

    // MaskStat analysis.
    updateMaskLevelForMaskStat(state, action: PayloadAction<{maskLevelForMaskStat: StatisticsOption['maskLevelForMaskStat']}>) {
      const { maskLevelForMaskStat } = action.payload;
      state.maskLevelForMaskStat = maskLevelForMaskStat;
    },
    updateMaskStatOption(state, action: PayloadAction<{maskStatOption: StatisticsOption['maskStatOption']}>) {
      const { maskStatOption } = action.payload;
      state.maskStatOption = maskStatOption;
    },
    updateMaskValuesForMaskStat(state, action: PayloadAction<{level: number; value: number}>) {
      const { level, value } = action.payload;
      state.maskValuesForMaskStat[level] = value;
    },
    updateMaskResolutionIdsForMaskStat(state, action: PayloadAction<{level: number; resolutionId: number}>) {
      const { level, resolutionId } = action.payload;
      state.maskResolutionIdsForMaskStat[level] = resolutionId;
    },
    updatePeriodAverageEnabled(state, action: PayloadAction<{periodAverageEnabled: StatisticsOption['periodAverageEnabled']}>) {
      const { periodAverageEnabled } = action.payload;
      state.periodAverageEnabled = periodAverageEnabled;
    },
    updateYearMax(state, action: PayloadAction<{yearMax: StatisticsOption['yearMax']}>) {
      const { yearMax } = action.payload;
      state.yearMax = yearMax;
    },
    updateYearMin(state, action: PayloadAction<{yearMin: StatisticsOption['yearMin']}>) {
      const { yearMin } = action.payload;
      state.yearMin = yearMin;
    },

    // List analysis.
    updateListOption(state, action: PayloadAction<{listOption: StatisticsOption['listOption']}>) {
      const { listOption } = action.payload;
      state.listOption = listOption;
    },
    updateMaskLevelForList(state, action: PayloadAction<{maskLevelForList: StatisticsOption['maskLevelForList']}>) {
      const { maskLevelForList } = action.payload;
      state.maskLevelForList = maskLevelForList;
    },
    updateMaskValuesForList(state, action: PayloadAction<{level: number; value: number}>) {
      const { level, value } = action.payload;
      state.maskValuesForList[level] = value;
    },
    updateMaskResolutionIdsForList(state, action: PayloadAction<{level: number; resolutionId: number}>) {
      const { level, resolutionId } = action.payload;
      state.maskResolutionIdsForList[level] = resolutionId;
    },
    updateTime(state, action: PayloadAction<{time: StatisticsOption['time']}>) {
      const { time } = action.payload;
      state.time = time;
    },
  },
});

export const {
  // Analysis mode.
  updateAnalysisMode,

  // MaskStat analysis.
  updateMaskLevelForMaskStat,
  updateMaskStatOption,
  updateMaskValuesForMaskStat,
  updateMaskResolutionIdsForMaskStat,
  updatePeriodAverageEnabled,
  updateYearMax,
  updateYearMin,

  // List analysis.
  updateListOption,
  updateMaskLevelForList,
  updateMaskValuesForList,
  updateMaskResolutionIdsForList,
  updateTime,
} = statisticsOptionSlice.actions;

export default statisticsOptionSlice.reducer;

// Analysis mode.
export const selectAnalysisMode = (state: RootState) => {
  const { analysisMode } = state.statisticsOption;
  return analysisMode;
};

// MaskStat analysis.
export const selectMaskLevelForMaskStat = (state: RootState) => {
  const { maskLevelForMaskStat } = state.statisticsOption;
  return maskLevelForMaskStat;
};

export const selectMaskStatOption = (state: RootState) => {
  const { maskStatOption } = state.statisticsOption;
  return maskStatOption;
};

export const selectMaskValuesForMaskStat = (state: RootState) => {
  const { maskValuesForMaskStat } = state.statisticsOption;
  return maskValuesForMaskStat;
};

export const selectMaskResolutionIdsForMaskStat = (state: RootState) => {
  const { maskResolutionIdsForMaskStat } = state.statisticsOption;
  return maskResolutionIdsForMaskStat;
};

export const selectPeriodAverageEnabled = (state: RootState) => {
  const { periodAverageEnabled } = state.statisticsOption;
  return periodAverageEnabled;
};

export const selectYearMax = (state: RootState) => {
  const { yearMax } = state.statisticsOption;
  return yearMax;
};

export const selectYearMin = (state: RootState) => {
  const { yearMin } = state.statisticsOption;
  return yearMin;
};

// List analysis.
export const selectListOption = (state: RootState) => {
  const { listOption } = state.statisticsOption;
  return listOption;
};

export const selectMaskLevelForList = (state: RootState) => {
  const { maskLevelForList } = state.statisticsOption;
  return maskLevelForList;
};

export const selectMaskValuesForList = (state: RootState) => {
  const { maskValuesForList } = state.statisticsOption;
  return maskValuesForList;
};

export const selectMaskResolutionIdsForList = (state: RootState) => {
  const { maskResolutionIdsForList } = state.statisticsOption;
  return maskResolutionIdsForList;
};

export const selectTime = (state: RootState) => {
  const { time } = state.statisticsOption;
  return time;
};

// PostData
export const selectPostData = (state: RootState) => {
  const {
    analysisMode,

    maskLevelForMaskStat,
    maskStatOption,
    maskValuesForMaskStat,
    maskResolutionIdsForMaskStat,
    periodAverageEnabled,
    yearMax,
    yearMin,

    listOption,
    maskLevelForList,
    maskValuesForList,
    maskResolutionIdsForList,
    time,
  } = state.statisticsOption;

  const maskid = (() => {
    if (analysisMode === 'maskstat') {
      const mask = maskLevelForMaskStat > -1;
      return mask ? constructMaskId(maskLevelForMaskStat, maskValuesForMaskStat[maskLevelForMaskStat], maskResolutionIdsForMaskStat[maskLevelForMaskStat]) : null;
    } else if (analysisMode === 'list') {
      return constructMaskId(maskLevelForList, maskValuesForList[maskLevelForList], maskResolutionIdsForList[maskLevelForList]);
    } else {
      throw new Error(`Unexpected analysisMode: ${analysisMode}`);
    }
  })();

  const ret: {[prop: string]: unknown} = {
    analysis: analysisMode,
    maskid,

    // MaskStat analysis.
    maskstatopt: maskStatOption,
    'period-average': periodAverageEnabled,
    yearrange: [yearMin, yearMax],

    // List analysis.
    listopt: listOption,
    time,
  };

  return ret;
};
